import { useQuery } from '@tanstack/react-query';
import { API_BASE, MAIN_LEAGUES } from '../utils';
import { apiFetch, serializeIds } from './fetch-utils';
import type {
  LeagueListResponse,
  LeagueStatsResponse,
  LeagueStatsbyDateResponse,
  LeagueStandingsResponse,
  BestLoaneesResponse,
  LongestDroughtsReponse,
  StatsLeadersResponse,
  StatMatchesResponse,
  PositionKingsResponse,
  PlayerZoneActionsResponse,
} from '../types/leagues';

// GET /leagues/
const fetchLeagues = () =>
  apiFetch<LeagueListResponse>(`${API_BASE}/leagues/`)

export const useFetchLeagues = () =>
  useQuery({
    queryKey: ['leagues'],
    queryFn: fetchLeagues,
    staleTime: 60 * 60 * 1000,
    retry: 2,
  })

// GET /leagues/stats/{season_year}
interface LeagueStatsParams {
  season_year: number;
  league_ids?: number[];
  stat?: string;
  limit?: number;
  country_id?: number;
  country2_id?: number;
  age?: number;
  position?: string;
}

const fetchLeagueStats = ({
  season_year,
  league_ids = MAIN_LEAGUES,
  stat = 'goals',
  limit = 15,
  country_id,
  country2_id,
  age,
  position,
}: LeagueStatsParams) => {
  const params = new URLSearchParams({ stat, limit: String(limit) });
  league_ids.forEach((id) => params.append('league_ids', String(id)));
  if (country_id !== undefined) params.set('country_id', String(country_id));
  if (country2_id !== undefined) params.set('country2_id', String(country2_id));
  if (age !== undefined) params.set('age', String(age));
  if (position !== undefined) params.set('position', position);
  return apiFetch<LeagueStatsResponse>(`${API_BASE}/leagues/stats/${season_year}?${params}`)
}

export const useFetchLeagueStats = (params: LeagueStatsParams) => {
  const league_ids = params.league_ids ?? MAIN_LEAGUES;
  return useQuery({
    queryKey: ['leagueStats', params.season_year, serializeIds(league_ids), params.stat, params.limit, params.country_id, params.country2_id, params.age, params.position],
    queryFn: () => fetchLeagueStats({ ...params, league_ids }),
    enabled: params.season_year > 0,
    staleTime: 15 * 60 * 1000,
    retry: 2,
  })
}

// GET /leagues/stats-bydate
interface LeagueStatsByDateParams {
  league_ids?: number[];
  start_date?: string;
  end_date?: string;
  stat?: string;
  limit?: number;
  age?: number;
  position?: string;
}

const fetchLeagueStatsByDate = ({
  league_ids = MAIN_LEAGUES,
  start_date,
  end_date,
  stat = 'goals',
  limit = 15,
  age,
  position,
}: LeagueStatsByDateParams) => {
  const params = new URLSearchParams({ stat, limit: String(limit) });
  league_ids.forEach((id) => params.append('league_ids', String(id)));
  if (start_date) params.set('start_date', start_date);
  if (end_date) params.set('end_date', end_date);
  if (age !== undefined) params.set('age', String(age));
  if (position !== undefined) params.set('position', position);
  return apiFetch<LeagueStatsbyDateResponse>(`${API_BASE}/leagues/stats-bydate?${params}`)
}

export const useFetchLeagueStatsByDate = (params: LeagueStatsByDateParams = {}) => {
  const league_ids = params.league_ids ?? MAIN_LEAGUES;
  return useQuery({
    queryKey: ['leagueStatsByDate', serializeIds(league_ids), params.start_date, params.end_date, params.stat, params.limit, params.age, params.position],
    queryFn: () => fetchLeagueStatsByDate({ ...params, league_ids }),
    staleTime: 15 * 60 * 1000,
    retry: 2,
  })
}

// GET /leagues/{league_id}/standings
interface LeagueStandingsParams {
  league_id: number;
  season_year: number;
}

const fetchLeagueStandings = ({ league_id, season_year }: LeagueStandingsParams) => {
  const params = new URLSearchParams({ season_year: String(season_year) });
  return apiFetch<LeagueStandingsResponse>(`${API_BASE}/leagues/${league_id}/standings?${params}`)
}

export const useFetchLeagueStandings = (params: LeagueStandingsParams) =>
  useQuery({
    queryKey: ['leagueStandings', params.league_id, params.season_year],
    queryFn: () => fetchLeagueStandings(params),
    enabled: params.league_id > 0 && params.season_year > 0,
    staleTime: 15 * 60 * 1000,
    retry: 2,
  })

// GET /leagues/{league_id}/position-kings
interface PositionKingsParams {
  league_id: number;
  zone: string;
  stat: string;
  season_year?: number;
}

const fetchPositionKings = ({ league_id, zone, stat, season_year = 2026 }: PositionKingsParams) => {
  const params = new URLSearchParams({ zone, stat, season_year: String(season_year) });
  return apiFetch<PositionKingsResponse>(`${API_BASE}/leagues/${league_id}/position-kings?${params}`)
}

export const useFetchPositionKings = (params: PositionKingsParams) =>
  useQuery({
    queryKey: ['positionKings', params.league_id, params.zone, params.stat, params.season_year],
    queryFn: () => fetchPositionKings(params),
    enabled: params.league_id > 0,
    staleTime: 15 * 60 * 1000,
    retry: 2,
  })

// GET /leagues/{league_id}/position-kings/actions
interface PlayerZoneActionsParams {
  league_id: number;
  player_id: number;
  zone: string;
  stat: string;
  season_year?: number;
}

const fetchPlayerZoneActions = ({ league_id, player_id, zone, stat, season_year = 2026 }: PlayerZoneActionsParams) => {
  const params = new URLSearchParams({ player_id: String(player_id), zone, stat, season_year: String(season_year) });
  return apiFetch<PlayerZoneActionsResponse>(`${API_BASE}/leagues/${league_id}/position-kings/actions?${params}`)
}

export const useFetchPlayerZoneActions = (params: PlayerZoneActionsParams) =>
  useQuery({
    queryKey: ['playerZoneActions', params.league_id, params.player_id, params.zone, params.stat, params.season_year],
    queryFn: () => fetchPlayerZoneActions(params),
    enabled: params.league_id > 0 && params.player_id > 0,
    staleTime: 15 * 60 * 1000,
    retry: 2,
  })

// GET /leagues/{league_id}/stats-leaders
interface LeagueStatsLeadersParams {
  league_id: number;
  season_year: number;
  stat?: string;
  limit?: number;
  position?: string;
  age?: number;
  // Aggregate across multiple leagues (e.g. Top-5) — overrides league_id.
  league_ids?: number[];
}

const fetchLeagueStatsLeaders = ({
  league_id,
  season_year,
  stat = 'goals',
  limit = 12,
  position,
  age,
  league_ids,
}: LeagueStatsLeadersParams) => {
  const params = new URLSearchParams({ season_year: String(season_year), stat, limit: String(limit) });
  if (position) params.set('position', position);
  if (age !== undefined) params.set('age', String(age));
  league_ids?.forEach((id) => params.append('league_ids', String(id)));
  return apiFetch<StatsLeadersResponse>(`${API_BASE}/leagues/${league_id}/stats-leaders?${params}`)
}

export const useFetchLeagueStatsLeaders = (params: LeagueStatsLeadersParams) =>
  useQuery({
    queryKey: [
      'leagueStatsLeaders',
      params.league_id,
      params.season_year,
      params.stat,
      params.limit,
      params.position,
      params.age,
      serializeIds(params.league_ids ?? []),
    ],
    queryFn: () => fetchLeagueStatsLeaders(params),
    enabled: params.league_id > 0 && params.season_year > 0,
    staleTime: 15 * 60 * 1000,
    retry: 2,
  })

// GET /leagues/players/{player_id}/stat-matches
interface PlayerStatMatchesParams {
  player_id: number;
  league_id: number;
  season_year: number;
  stat?: string;
  limit?: number;
  enabled?: boolean;
  league_ids?: number[];
}

const fetchPlayerStatMatches = ({
  player_id,
  league_id,
  season_year,
  stat = 'goals',
  limit = 4,
  league_ids,
}: PlayerStatMatchesParams) => {
  const params = new URLSearchParams({
    league_id: String(league_id),
    season_year: String(season_year),
    stat,
    limit: String(limit),
  });
  league_ids?.forEach((id) => params.append('league_ids', String(id)));
  return apiFetch<StatMatchesResponse>(`${API_BASE}/leagues/players/${player_id}/stat-matches?${params}`)
}

export const useFetchPlayerStatMatches = (params: PlayerStatMatchesParams) =>
  useQuery({
    queryKey: [
      'playerStatMatches',
      params.player_id,
      params.league_id,
      params.season_year,
      params.stat,
      params.limit,
      serializeIds(params.league_ids ?? []),
    ],
    queryFn: () => fetchPlayerStatMatches(params),
    enabled:
      (params.enabled ?? true) &&
      params.player_id > 0 &&
      params.league_id > 0 &&
      params.season_year > 0,
    staleTime: 15 * 60 * 1000,
    retry: 2,
  })

// GET /leagues/best-loanees
interface BestLoaneesParams {
  start_date?: string;
  end_date?: string;
  stat?: string;
  limit?: number;
}

const fetchBestLoanees = ({
  start_date = '2025-07-01',
  end_date = '2026-06-30',
  stat = 'goals',
  limit = 20,
}: BestLoaneesParams) => {
  const params = new URLSearchParams({ start_date, end_date, stat, limit: String(limit) });
  return apiFetch<BestLoaneesResponse>(`${API_BASE}/leagues/best-loanees?${params}`)
}

export const useFetchBestLoanees = (params: BestLoaneesParams = {}) =>
  useQuery({
    queryKey: ['bestLoanees', params.start_date, params.end_date, params.stat, params.limit],
    queryFn: () => fetchBestLoanees(params),
    staleTime: 15 * 60 * 1000,
    retry: 2,
  })

// GET /leagues/droughts/{season_year}
interface LeagueDroughtsParams {
  season_year: number;
  league_ids?: number[];
  stat?: string;
  limit?: number;
}

const fetchLeagueDroughts = ({
  season_year,
  league_ids = MAIN_LEAGUES,
  stat = 'goals',
  limit = 15,
}: LeagueDroughtsParams) => {
  const params = new URLSearchParams({ stat, limit: String(limit) });
  league_ids.forEach((id) => params.append('league_ids', String(id)));
  return apiFetch<LongestDroughtsReponse>(`${API_BASE}/leagues/droughts/${season_year}?${params}`)
}

export const useFetchLeagueDroughts = (params: LeagueDroughtsParams) => {
  const league_ids = params.league_ids ?? MAIN_LEAGUES;
  return useQuery({
    queryKey: ['leagueDroughts', params.season_year, serializeIds(league_ids), params.stat, params.limit],
    queryFn: () => fetchLeagueDroughts({ ...params, league_ids }),
    enabled: params.season_year > 0,
    staleTime: 15 * 60 * 1000,
    retry: 2,
  })
}
